export const DEFAULT_MAX_TRIAL_DAYS = 7;

export const maxTrialDaysFor = (userData = {}) => {
  const days = Number(userData?.max_trial_days);
  return days > 0 ? days : DEFAULT_MAX_TRIAL_DAYS;
};

export const canStartMaxUpgradeTrial = (userData = {}) => {
  const billingPlanTier =
    userData?.billing_subscription_plan_tier || userData?.subscription_plan_tier;

  return (
    userData?.user_id != null &&
    billingPlanTier !== "max" &&
    userData?.max_trial_eligible === true &&
    !userData?.max_trial_active
  );
};

// Trial mode is for starting a trial. An active trial uses the normal plan
// page so the user can choose paid MAX (or Pro) before or after it ends; the
// trial itself never bills or converts.
export const shouldUseMaxTrialMode = ({
  maxTrialEligible,
  maxTrialRequested,
  trialIsActive,
  userData = {},
  userDataResolved,
} = {}) => (
  !trialIsActive && (
    (Boolean(maxTrialRequested) && !userDataResolved) ||
    (
      Boolean(maxTrialRequested) &&
      canStartMaxUpgradeTrial({
        ...userData,
        max_trial_eligible: maxTrialEligible,
      })
    )
  )
);

const DAY_MS = 24 * 60 * 60 * 1000;

const hasPaidMax = (userData = {}) =>
  (userData?.billing_subscription_plan_tier || userData?.subscription_plan_tier) === "max" &&
  Boolean(userData?.has_active_subscription);

// What the workspace says about the user's MAX trial: days left while it
// runs, then an explicit continuation for a short window after it ends.
export const maxTrialStatusFor = (userData = {}, now = Date.now()) => {
  if (userData?.user_id == null || hasPaidMax(userData)) return null;

  if (userData?.max_trial_active && userData?.max_trial_ends_at) {
    const endsAt = new Date(userData.max_trial_ends_at).getTime();
    if (!Number.isFinite(endsAt) || endsAt <= now) return null;
    const daysLeft = Math.max(1, Math.ceil((endsAt - now) / DAY_MS));
    return {
      state: "active",
      daysLeft,
      endsAt: userData.max_trial_ends_at,
      title: daysLeft === 1 ? "MAX trial ends today" : `MAX trial · ${daysLeft} days left`,
      actionLabel: "Keep MAX",
    };
  }

  if (userData?.max_trial_recently_ended && userData?.max_trial_ended_at) {
    return {
      state: "ended",
      daysLeft: 0,
      endsAt: userData.max_trial_ended_at,
      title: "Your MAX trial ended",
      actionLabel: "Keep MAX",
    };
  }

  return null;
};

export const maxTrialContinuationPathFor = (userData = {}) => {
  const offer = userData?.max_trial_promotion_offer;
  return offer
    ? `/plan-selection?offer=${encodeURIComponent(offer)}#max`
    : "/plan-selection#max";
};

export const shouldShowMaxUpgradeCta = (userData = {}) => {
  const billingPlanTier =
    userData?.billing_subscription_plan_tier || userData?.subscription_plan_tier;
  const nonMaxStripeSubscriber =
    Boolean(userData?.has_active_subscription) &&
    userData?.subscription_provider === "stripe" &&
    billingPlanTier !== "max";

  return (
    nonMaxStripeSubscriber &&
    ["active", "trialing"].includes(userData?.subscription_status) &&
    !userData?.max_trial_active
  );
};

export const maxUpgradePathFor = (userData = {}) =>
  canStartMaxUpgradeTrial(userData)
    ? "/plan-selection?max_trial=1#max"
    : "/plan-selection#max";

export const maxUpgradeButtonLabelFor = (userData = {}) =>
  canStartMaxUpgradeTrial(userData) ? "Try Max" : "Go Max";

export const maxTrialPrimaryLabelFor = (userData = {}) =>
  `Try MAX ${maxTrialDaysFor(userData)} Days Free`;

export const buildMaxUpgradeModalProps = (userData = {}, copy = {}) => {
  if (!canStartMaxUpgradeTrial(userData)) {
    return {
      eyebrow: copy.eyebrow || "Incite Max",
      title: copy.title,
      description: copy.description,
      bullets: copy.bullets,
      footer: copy.footer,
      primaryLabel: copy.primaryLabel || "Get Max",
      secondaryLabel: copy.secondaryLabel,
    };
  }

  const days = maxTrialDaysFor(userData);
  const activeProSubscription = Boolean(userData?.has_active_subscription);

  return {
    eyebrow: `${days}-Day MAX Trial`,
    title: copy.trialTitle || `Try MAX free for ${days} days.`,
    description: copy.trialDescription || copy.description,
    bullets: copy.trialBullets || copy.bullets,
    footer:
      copy.trialFooter ||
      (activeProSubscription
        ? `Today is $0. Your Pro subscription stays active during the ${days}-day trial.`
        : `Today is $0. No payment is collected. You can choose paid MAX before or after the ${days}-day trial ends; billing starts when you subscribe.`),
    primaryLabel: copy.trialPrimaryLabel || maxTrialPrimaryLabelFor(userData),
    secondaryLabel: copy.secondaryLabel,
  };
};
